// ═══════════════════════════════════════════════════════════════
// RichTextEditor.tsx – egyszerű formázó szövegszerkesztő (TipTap / ProseMirror)
//
// Félkövér, dőlt, aláhúzott, áthúzott, betűméret (% / px), betűszín, kiemelés, címsor, felsorolás,
// számozott lista, igazítás, link, kép (beágyazva, automatikusan kicsinyítve),
// egyszerű táblázat, visszavonás – és HTML-forrás nézet.
// A kimenet HTML; mentéskor a szerver, megjelenítéskor a felület is szűri.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extensions';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link2, Image as ImageIcon, Table as TableIcon, Undo2, Redo2, Code2, RemoveFormatting, Highlighter, Baseline,
  Rows3, Columns3, Trash2, FileUp, Check, X, ALargeSmall, Minus, Plus,
} from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const TEXT_COLORS = ['#0f172a', '#475569', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#db2777'];
const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#e9d5ff'];
const MAX_IMG_SIDE = 1600;
const SIZE_PERCENTS = [75, 90, 100, 110, 125, 150, 175, 200];
const SIZE_PX = [10, 12, 14, 16, 18, 20, 24, 28, 32];

/** a kijelölés betűmérete → következő lépés (%-ban 10-es, px-ben 2-es lépéssel); üres = alapméret (100%) */
export function stepFontSize(current: string | null | undefined, dir: 1 | -1): string | null {
  const m = /^(\d+(?:\.\d+)?)(px|%)$/.exec(String(current || '').trim());
  if (m && m[2] === 'px') { const v = Math.min(96, Math.max(6, Number(m[1]) + dir * 2)); return `${v}px`; }
  const pct = m ? Number(m[1]) : 100;
  const v = Math.min(400, Math.max(40, Math.round(pct / 10) * 10 + dir * 10));
  return v === 100 ? null : `${v}%`;
}

/** kép kicsinyítése beágyazáshoz (data URL); a PNG/GIF kis méretben változatlan marad */
async function imageFileToDataUrl(file: File): Promise<string> {
  const raw: string = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file); });
  if (file.size < 200_000 && /png|gif|webp/.test(file.type)) return raw;
  const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new window.Image(); i.onload = () => res(i); i.onerror = rej; i.src = raw; });
  const scale = Math.min(1, MAX_IMG_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale);
  const ctx = c.getContext('2d');
  if (!ctx) return raw;
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const out = c.toDataURL('image/jpeg', 0.85);
  return out.length < raw.length ? out : raw;
}

/** olvasható HTML-forrás (blokkelemek külön sorban) */
function prettyHtml(html: string): string {
  return html.replace(/></g, '>\n<').replace(/\n<(\/?(strong|em|u|s|a|span|mark|code|br)\b)/g, '<$1');
}

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** „Betöltés fájlból” gomb (PDF/DOCX → tartalom) */
  onImportFile?: () => void;
  /** további eszköztár-elemek (pl. változó beszúrása) – az editort megkapják */
  extraTools?: (editor: Editor) => React.ReactNode;
  id?: string;
  /** a szerkesztőfelület legnagyobb magassága (CSS érték), pl. nagy ablakban több hely */
  contentMaxHeight?: string;
  /** a szerkesztőfelület legkisebb magassága (nagy ablakban kitölti a helyet) */
  contentMinHeight?: string;
}

const Btn: React.FC<{ on?: boolean; title: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({ on, title, onClick, disabled, children }) => (
  <button type="button" title={title} aria-label={title} aria-pressed={on} disabled={disabled}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`p-1.5 rounded-md transition cursor-pointer disabled:opacity-30 disabled:cursor-default ${on ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-700/60'}`}>
    {children}
  </button>
);
const Sep = () => <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-0.5" aria-hidden="true" />;

export const RichTextEditor: React.FC<Props> = ({ value, onChange, placeholder, onImportFile, extraTools, id, contentMaxHeight = '50vh', contentMinHeight }) => {
  const [source, setSource] = useState<string | null>(null); // nem null = HTML nézet
  const [popover, setPopover] = useState<null | 'color' | 'highlight' | 'link' | 'image' | 'size'>(null);
  const [pxInput, setPxInput] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imgError, setImgError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastEmitted = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, defaultProtocol: 'https', protocols: ['mailto', 'tel'] } }),
      TextStyle,
      Color,
      FontSize,
      Highlight.configure({ multicolor: true }),
      Image.configure({ inline: false, allowBase64: true }),
      TableKit.configure({ table: { resizable: false } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || 'Írd ide a közzéteendő szöveget…' }),
    ],
    content: value || '',
    editorProps: { attributes: { class: 'rich-text text-sm text-slate-900 dark:text-slate-100 px-3.5 py-2.5', ...(id ? { id } : {}), 'aria-label': 'Formázott leírás' } },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? '' : ed.getHTML();
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // kívülről érkező tartalom (pl. fájlból betöltés) → a szerkesztőbe
  useEffect(() => {
    if (!editor || source !== null) return;
    if ((value || '') !== lastEmitted.current) {
      lastEmitted.current = value || '';
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor, source]);

  if (!editor) return null;

  const toggleSource = () => {
    if (source === null) {
      setSource(prettyHtml(editor.isEmpty ? '' : editor.getHTML()));
      setPopover(null);
    } else {
      const clean = sanitizeHtml(source.replace(/>\s*\n\s*</g, '><'));
      editor.commands.setContent(clean, { emitUpdate: false });
      const html = editor.isEmpty ? '' : editor.getHTML();
      lastEmitted.current = html;
      onChange(html);
      setSource(null);
    }
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else {
      const href = /^(https?:|mailto:|tel:)/i.test(url) ? url : (url.includes('@') && !url.includes('/') ? `mailto:${url}` : `https://${url}`);
      if (editor.state.selection.empty) editor.chain().focus().insertContent(`<a href="${href.replace(/"/g, '&quot;')}">${url.replace(/[<>&]/g, '')}</a>`).run();
      else editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setPopover(null);
  };

  const insertImageFile = async (f: File | undefined) => {
    setImgError(null);
    if (!f) return;
    if (!f.type.startsWith('image/')) { setImgError('Csak képfájl illeszthető be'); return; }
    try {
      const src = await imageFileToDataUrl(f);
      if (src.length > 900_000) { setImgError('A kép kicsinyítve is túl nagy (~0,9 MB felett) – tölts fel kisebbet, vagy csatold mellékletként.'); return; }
      editor.chain().focus().setImage({ src, alt: f.name.replace(/\.[^.]+$/, '') }).run();
      setPopover(null);
    } catch { setImgError('A kép nem olvasható'); }
  };

  const inTable = editor.isActive('table');
  const curSize: string = editor.getAttributes('textStyle').fontSize || '';
  const applySize = (v: string | null) => {
    if (v) editor.chain().focus().setFontSize(v).run(); else editor.chain().focus().unsetFontSize().run();
  };
  const applyPx = () => {
    const n = Number(String(pxInput).replace(',', '.'));
    if (!Number.isFinite(n) || n < 6 || n > 96) return;
    applySize(`${Math.round(n * 10) / 10}px`);
    setPopover(null);
  };
  const block = editor.isActive('heading', { level: 2 }) ? 'h2' : editor.isActive('heading', { level: 3 }) ? 'h3' : 'p';

  return (
    <div className="rich-editor rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 focus-within:ring-2 focus-within:ring-indigo-500 overflow-visible">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 rounded-t-xl relative" role="toolbar" aria-label="Formázás">
        {source === null && (
          <>
            <select value={block} aria-label="Bekezdéstípus"
              onChange={(e) => { const v = e.target.value; if (v === 'p') editor.chain().focus().setParagraph().run(); else editor.chain().focus().toggleHeading({ level: v === 'h2' ? 2 : 3 }).run(); }}
              className="text-[11px] bg-transparent border border-slate-200 dark:border-slate-700 rounded-md px-1 py-1 text-slate-700 dark:text-slate-300 mr-0.5">
              <option value="p">Normál</option><option value="h2">Címsor</option><option value="h3">Alcím</option>
            </select>
            <Btn title="Félkövér (Ctrl+B)" on={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-3.5 h-3.5" /></Btn>
            <Btn title="Dőlt (Ctrl+I)" on={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-3.5 h-3.5" /></Btn>
            <Btn title="Aláhúzott (Ctrl+U)" on={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-3.5 h-3.5" /></Btn>
            <Btn title="Áthúzott" on={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-3.5 h-3.5" /></Btn>
            <Btn title="Betűméret csökkentése (−10%)" onClick={() => applySize(stepFontSize(curSize, -1))}><Minus className="w-3 h-3" /></Btn>
            <span className="relative">
              <button type="button" title="Betűméret (százalékos nagyítás / px)" aria-label="Betűméret" aria-expanded={popover === 'size'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setPxInput(/px$/.test(curSize) ? curSize.replace('px', '') : ''); setPopover(popover === 'size' ? null : 'size'); }}
                className={`inline-flex items-center gap-0.5 px-1.5 py-1 rounded-md text-[11px] font-semibold tabular-nums cursor-pointer ${popover === 'size' || curSize ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-700/60'}`}>
                <ALargeSmall className="w-3.5 h-3.5" /> {curSize || '100%'}
              </button>
              {popover === 'size' && (
                <div className="absolute z-20 top-8 left-0 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl w-60 space-y-2 text-xs">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 mb-1">Nagyítás / kicsinyítés</div>
                    <div className="grid grid-cols-4 gap-1">
                      {SIZE_PERCENTS.map((p) => (
                        <button key={p} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { applySize(p === 100 ? null : `${p}%`); setPopover(null); }}
                          className={`px-1 py-1 rounded border text-[11px] cursor-pointer ${(curSize || '100%') === `${p}%` ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/50 font-bold' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>{p}%</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-500 mb-1">Pontos méret (px)</div>
                    <div className="grid grid-cols-5 gap-1 mb-1">
                      {SIZE_PX.map((p) => (
                        <button key={p} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { applySize(`${p}px`); setPopover(null); }}
                          className={`px-1 py-1 rounded border text-[11px] cursor-pointer ${curSize === `${p}px` ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/50 font-bold' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>{p}</button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" min={6} max={96} step={0.5} value={pxInput} onChange={(e) => setPxInput(e.target.value)} placeholder="pl. 15" aria-label="Betűméret pixelben"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPx(); } if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPopover(null); } }}
                        className="w-20 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent" />
                      <span className="text-slate-400">px</span>
                      <Btn title="Alkalmaz" onClick={applyPx}><Check className="w-3.5 h-3.5" /></Btn>
                    </div>
                  </div>
                  <button type="button" onClick={() => { applySize(null); setPopover(null); }} className="text-[10px] text-slate-500 hover:underline cursor-pointer">Alapméret (100%)</button>
                </div>
              )}
            </span>
            <Btn title="Betűméret növelése (+10%)" onClick={() => applySize(stepFontSize(curSize, 1))}><Plus className="w-3 h-3" /></Btn>
            <span className="relative">
              <Btn title="Betűszín" on={popover === 'color'} onClick={() => setPopover(popover === 'color' ? null : 'color')}>
                <Baseline className="w-3.5 h-3.5" style={{ color: editor.getAttributes('textStyle').color || undefined }} />
              </Btn>
              {popover === 'color' && (
                <div className="absolute z-20 top-8 left-0 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl flex flex-wrap gap-1 w-44">
                  {TEXT_COLORS.map((c) => <button key={c} type="button" title={c} aria-label={`Szín ${c}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().setColor(c).run(); setPopover(null); }} className="w-6 h-6 rounded border border-slate-200 cursor-pointer" style={{ background: c }} />)}
                  <label className="w-6 h-6 rounded border border-dashed border-slate-300 cursor-pointer overflow-hidden" title="Egyéni szín">
                    <input type="color" className="opacity-0 w-6 h-6 cursor-pointer" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
                  </label>
                  <button type="button" onClick={() => { editor.chain().focus().unsetColor().run(); setPopover(null); }} className="text-[10px] w-full text-left text-slate-500 hover:underline cursor-pointer">Alapszín</button>
                </div>
              )}
            </span>
            <span className="relative">
              <Btn title="Kiemelés" on={editor.isActive('highlight') || popover === 'highlight'} onClick={() => setPopover(popover === 'highlight' ? null : 'highlight')}><Highlighter className="w-3.5 h-3.5" /></Btn>
              {popover === 'highlight' && (
                <div className="absolute z-20 top-8 left-0 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl flex flex-wrap gap-1 w-40">
                  {HIGHLIGHTS.map((c) => <button key={c} type="button" title={c} aria-label={`Kiemelés ${c}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setPopover(null); }} className="w-6 h-6 rounded border border-slate-200 cursor-pointer" style={{ background: c }} />)}
                  <button type="button" onClick={() => { editor.chain().focus().unsetHighlight().run(); setPopover(null); }} className="text-[10px] w-full text-left text-slate-500 hover:underline cursor-pointer">Kiemelés törlése</button>
                </div>
              )}
            </span>
            <Sep />
            <Btn title="Felsorolás" on={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></Btn>
            <Btn title="Számozott lista" on={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></Btn>
            <Btn title="Balra igazítás" on={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft className="w-3.5 h-3.5" /></Btn>
            <Btn title="Középre igazítás" on={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter className="w-3.5 h-3.5" /></Btn>
            <Btn title="Jobbra igazítás" on={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight className="w-3.5 h-3.5" /></Btn>
            <Sep />
            <span className="relative">
              <Btn title="Hivatkozás" on={editor.isActive('link') || popover === 'link'} onClick={() => { setLinkUrl(editor.getAttributes('link').href || ''); setPopover(popover === 'link' ? null : 'link'); }}><Link2 className="w-3.5 h-3.5" /></Btn>
              {popover === 'link' && (
                <div className="absolute z-20 top-8 left-0 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl flex items-center gap-1 w-72">
                  <input autoFocus value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://… vagy e-mail-cím"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPopover(null); } }}
                    className="flex-1 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent" aria-label="Hivatkozás címe" />
                  <Btn title="Alkalmaz" onClick={applyLink}><Check className="w-3.5 h-3.5" /></Btn>
                  <Btn title="Mégse" onClick={() => setPopover(null)}><X className="w-3.5 h-3.5" /></Btn>
                </div>
              )}
            </span>
            <span className="relative">
              <Btn title="Kép beszúrása" on={popover === 'image'} onClick={() => { setImgError(null); setImgUrl(''); setPopover(popover === 'image' ? null : 'image'); }}><ImageIcon className="w-3.5 h-3.5" /></Btn>
              {popover === 'image' && (
                <div className="absolute z-20 top-8 left-0 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl w-72 space-y-1.5 text-xs">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => { insertImageFile(e.target.files?.[0]); e.target.value = ''; }} />
                  <button type="button" onClick={() => fileRef.current?.click()} className="w-full px-2 py-1.5 rounded bg-indigo-600 text-white font-semibold cursor-pointer">Kép feltöltése a gépről…</button>
                  <div className="flex items-center gap-1">
                    <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="vagy kép címe (https://…)" aria-label="Kép címe"
                      className="flex-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-transparent"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (/^https:\/\//i.test(imgUrl)) { editor.chain().focus().setImage({ src: imgUrl }).run(); setPopover(null); } else setImgError('Csak https:// kezdetű cím'); } }} />
                    <Btn title="Beszúrás" onClick={() => { if (/^https:\/\//i.test(imgUrl)) { editor.chain().focus().setImage({ src: imgUrl }).run(); setPopover(null); } else setImgError('Csak https:// kezdetű cím'); }}><Check className="w-3.5 h-3.5" /></Btn>
                  </div>
                  <p className="text-[10px] text-slate-400">A kép a leírásba ágyazódik (nagyobb képet automatikusan kicsinyít).</p>
                  {imgError && <p className="text-[11px] text-rose-600" role="alert">{imgError}</p>}
                </div>
              )}
            </span>
            <Btn title="Táblázat beszúrása (3×3, fejléccel)" on={inTable} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon className="w-3.5 h-3.5" /></Btn>
            {inTable && (
              <span className="inline-flex items-center gap-0.5 px-1 rounded-md bg-indigo-50 dark:bg-indigo-950/40">
                <Btn title="Sor hozzáadása alá" onClick={() => editor.chain().focus().addRowAfter().run()}><Rows3 className="w-3.5 h-3.5" /></Btn>
                <Btn title="Oszlop hozzáadása jobbra" onClick={() => editor.chain().focus().addColumnAfter().run()}><Columns3 className="w-3.5 h-3.5" /></Btn>
                <Btn title="Sor törlése" onClick={() => editor.chain().focus().deleteRow().run()}><span className="text-[10px] font-bold">−S</span></Btn>
                <Btn title="Oszlop törlése" onClick={() => editor.chain().focus().deleteColumn().run()}><span className="text-[10px] font-bold">−O</span></Btn>
                <Btn title="Táblázat törlése" onClick={() => editor.chain().focus().deleteTable().run()}><Trash2 className="w-3.5 h-3.5" /></Btn>
              </span>
            )}
            <Sep />
            <Btn title="Formázás törlése" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><RemoveFormatting className="w-3.5 h-3.5" /></Btn>
            <Btn title="Visszavonás (Ctrl+Z)" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-3.5 h-3.5" /></Btn>
            <Btn title="Újra (Ctrl+Y)" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-3.5 h-3.5" /></Btn>
            {onImportFile && (<><Sep /><Btn title="Tartalom betöltése fájlból (PDF, DOCX, TXT, HTML)" onClick={onImportFile}><FileUp className="w-3.5 h-3.5" /></Btn></>)}
            {extraTools && <span className="ml-0.5">{extraTools(editor)}</span>}
          </>
        )}
        <span className="ml-auto">
          <button type="button" onClick={toggleSource} aria-pressed={source !== null}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold cursor-pointer ${source !== null ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-700/60'}`}
            title={source !== null ? 'Vissza a formázott nézetre' : 'HTML-forrás megtekintése és szerkesztése'}>
            <Code2 className="w-3.5 h-3.5" /> HTML
          </button>
        </span>
      </div>
      {source !== null ? (
        <textarea value={source} onChange={(e) => { setSource(e.target.value); onChange(e.target.value); }} spellCheck={false}
          aria-label="HTML-forrás"
          style={{ height: contentMaxHeight === '50vh' ? undefined : contentMaxHeight }}
          className="w-full min-h-[12rem] bg-transparent font-mono text-[11px] leading-relaxed text-slate-800 dark:text-slate-200 px-3.5 py-2.5 outline-none resize-y rounded-b-xl" />
      ) : (
        <div className="overflow-y-auto flex flex-col" style={{ maxHeight: contentMaxHeight, minHeight: contentMinHeight }} onClick={() => setPopover(null)}>
          <EditorContent editor={editor} className="flex-1 flex flex-col [&>.ProseMirror]:flex-1" />
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
